import { Dialog } from "@base-ui/react/dialog";
import { Command } from "cmdk";
import type { ReactNode } from "react";
import { paletteScore } from "./palette-match";

// ⌘K (design-system.md §4.10): routes, entities, actions on the open entity. The app decides the items, and lists
// only the actions the user is allowed to run; running one calls the same mutation as its button.

export interface PaletteItem {
  id: string;
  label: string;
  /** Extra words the search matches (codes, synonyms). */
  keywords?: string[];
  icon?: ReactNode;
  /** Where the item belongs, after the label in a quieter tone (the section of a place). */
  hint?: string;
  /** What the row shows in place of the label (a found text with its match marked); the label still names it. */
  content?: ReactNode;
  /**
   * An item the server found, with its score from 0 to 1: it is not filtered again on the client, and the items of a
   * group keep the order of their ranks. Without it, the palette matches the label and the keywords itself.
   */
  rank?: number;
  shortcut?: ReactNode;
  run: () => void;
}

export interface PaletteGroup {
  heading: string;
  items: PaletteItem[];
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: PaletteGroup[];
  label: string;
  placeholder: string;
  emptyText: string;
  /** Typed text, for groups that search the server (entities by code). */
  search?: string;
  onSearchChange?: (value: string) => void;
}

// cmdk hides an item that scores 0, and sorts the items of a group and the groups by score
const MIN_RANK = 0.001;

const itemValue = (item: PaletteItem) => `${item.id} ${item.label}`.trim();

export function CommandPalette(props: CommandPaletteProps) {
  const { open, onOpenChange, groups, label, placeholder, emptyText } = props;
  const run = (item: PaletteItem) => {
    onOpenChange(false);
    item.run();
  };
  const ranks = new Map<string, number>();
  const labels = new Map<string, string>();
  for (const item of groups.flatMap((g) => g.items)) {
    if (item.rank !== undefined) ranks.set(itemValue(item), Math.max(item.rank, MIN_RANK));
    labels.set(itemValue(item), item.label);
  }
  // the value carries the id, which is not something the user reads: only the label and the keywords match
  const filter = (value: string, search: string, keywords?: string[]) =>
    ranks.get(value) ?? paletteScore(labels.get(value) ?? value, search, keywords);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-60 bg-black/20 dark:bg-black/50" />
        <Dialog.Popup className="fixed top-[15vh] left-1/2 z-60 w-[36rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-dialog">
          <Dialog.Title className="sr-only">{label}</Dialog.Title>
          <Command label={label} loop filter={filter}>
            <Command.Input
              {...(props.search !== undefined && { value: props.search })}
              {...(props.onSearchChange && { onValueChange: props.onSearchChange })}
              placeholder={placeholder}
              className="h-11 w-full border-b border-border bg-transparent px-4 text-sm outline-none placeholder:text-muted-foreground"
            />
            <Command.List className="max-h-[min(20rem,calc(85vh-3rem))] overflow-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</Command.Empty>
              {groups
                .filter((g) => g.items.length > 0)
                .map((group) => (
                  <Command.Group
                    key={group.heading}
                    heading={group.heading}
                    className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    {group.items.map((item) => (
                      <Command.Item
                        // cmdk scores an item once per value and search: a new rank is a new item
                        key={`${item.id}:${item.rank ?? ""}`}
                        value={itemValue(item)}
                        keywords={item.keywords ?? []}
                        onSelect={() => run(item)}
                        className="flex h-9 cursor-default pointer-coarse:h-(--control-h) items-center gap-2.5 rounded-md px-2.5 text-sm data-[selected=true]:bg-accent [&_svg]:size-4 [&_svg]:text-muted-foreground"
                      >
                        {item.icon}
                        <span className="min-w-0 flex-1 truncate">
                          {item.content ?? item.label}
                          {item.hint && <span className="ml-2 text-xs text-muted-foreground">{item.hint}</span>}
                        </span>
                        {item.shortcut}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
            </Command.List>
          </Command>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
